import ShareIcon from '@mui/icons-material/Share';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import { Breakpoint } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCopyToClipboard } from '@/utils/copyToClipboard';
import { useBreakpoint } from '@/utils/useBreakpoint';

import { Modal } from '../Modal/Modal';

import { shareProfileStyles as sx } from './Share.styles';

const TIMEOUT_THRESHOLD = 1250;

const getUrl = () => {
	const sectionIndex = window.location.href.indexOf('#');

	if (sectionIndex === -1) {
		return window.location.href;
	}

	return window.location.href.slice(0, sectionIndex);
};

type Props = {
	buttonText?: string;
	title: string;
	description: string;
	collapseAt?: Breakpoint;
	loading?: boolean;
};

export const Share = ({
	buttonText,
	title,
	description,
	collapseAt,
	loading
}: Props) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [url, setUrl] = useState('');
	const { copyToClipboard } = useCopyToClipboard(false);
	const [isCopyPending, setIsCopyPending] = useState(false);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isCollapsed = useBreakpoint(collapseAt ?? 'md', 'down');

	const clearPending = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	};

	const handleOpen = () => {
		setUrl(getUrl());
		setOpen(true);
		setIsCopyPending(false);
		clearPending();
	};

	const handleClose = () => {
		setOpen(false);
		setIsCopyPending(false);
		clearPending();
	};

	const handleShareClick = useCallback(() => {
		clearPending();
		copyToClipboard(url);
		setIsCopyPending(true);

		timeoutRef.current = setTimeout(() => {
			setIsCopyPending(false);
			timeoutRef.current = null;
		}, TIMEOUT_THRESHOLD);
	}, [copyToClipboard, url]);

	useEffect(() => () => clearPending(), []);

	if (loading) {
		return isCollapsed ? (
			<Skeleton animation={false} width={28} height={28} variant='circular' />
		) : (
			<Skeleton animation={false} width={130} />
		);
	}

	return (
		<>
			{isCollapsed ? (
				<Tooltip title={buttonText ?? t('Share')}>
					<IconButton onClick={handleOpen} color='primary' size='small'>
						<ShareIcon fontSize='inherit' />
					</IconButton>
				</Tooltip>
			) : (
				<Button
					sx={sx.root}
					variant='contained'
					startIcon={<ShareIcon />}
					onClick={handleOpen}
				>
					{buttonText ?? t('Share')}
				</Button>
			)}

			<Modal open={open} onClose={handleClose} title={title}>
				<Box sx={sx.urlContainer}>
					<Box sx={sx.urlText}>{url}</Box>
					<Button
						variant='contained'
						sx={sx.copyButton}
						onClick={handleShareClick}
						disabled={isCopyPending}
					>
						{isCopyPending ? t('Copied to clipboard') : t('Copy Link')}
					</Button>
				</Box>
				<Typography id='modal-description' sx={sx.info}>
					{description}
				</Typography>
			</Modal>
		</>
	);
};
